'use client';

import ReactDOM from 'react-dom';
import { X, BookOpen } from 'lucide-react';
import { useEffect } from 'react';
import { trackEvent } from '@/lib/analytics';
import useAuthStore from '@/store/useAuthStore';

interface SubjectPaywallModalProps {
  visible: boolean;
  blockedSubject: string;
  onClose: () => void;
}

const SUBJECT_LABELS: Record<string, string> = {
  math: 'Math',
  science: 'Science',
  history: 'Social Studies',
  vocabulary: 'Vocabulary',
  grammar: 'Grammar',
  'amc-math': 'AMC Math',
};

export default function SubjectPaywallModal({ visible, blockedSubject, onClose }: SubjectPaywallModalProps) {
  const profile = useAuthStore((state) => state.profile);
  const lockedSubject = profile?.free_subject;
  useEffect(() => {
    if (visible) {
      trackEvent('subject_paywall_shown', { blocked_subject: blockedSubject, locked_subject: lockedSubject });
    }
  }, [visible, blockedSubject, lockedSubject]);

  const handleClose = () => {
    trackEvent('subject_paywall_dismissed', { blocked_subject: blockedSubject });
    onClose();
  };

  if (!visible) return null;

  const lockedLabel = lockedSubject ? (SUBJECT_LABELS[lockedSubject] ?? lockedSubject) : null;
  const blockedLabel = SUBJECT_LABELS[blockedSubject] ?? blockedSubject;

  return (
    <>
      {ReactDOM.createPortal(
        <div
          className="fixed inset-0"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="fixed left-1/2 top-1/2 z-[1001] w-full max-w-[calc(100vw-1rem)] -translate-x-1/2 -translate-y-1/2 px-2 sm:max-w-[calc(100vw-2rem)] sm:px-0">
            <div
              className="relative mx-auto w-[340px] animate-fade-in rounded-2xl bg-white px-8 pb-6 pt-8 shadow-lg md:w-[420px]"
              style={{ maxWidth: '100%' }}
              onClick={(e) => e.stopPropagation()}
            >
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-3 top-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:bg-[#F3F4F6] hover:text-[#374151]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF3E0]">
              <BookOpen className="h-8 w-8 text-[#FBAF2C]" />
            </div>

            <div>
              <h2 className="display-text-bold text-lg text-[#1e1e20]">
                Want to Learn More Subjects?
              </h2>
              <p className="display-text mt-2 text-sm leading-relaxed text-[#6B7280]">
                {lockedLabel
                  ? `Your free plan is locked to ${lockedLabel}. Ask your parent to upgrade so you can access ${blockedLabel} and more!`
                  : `Ask your parent to upgrade so you can access ${blockedLabel} and more!`}
              </p>
            </div>

            <div className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
              <p className="display-text-semi text-sm text-[#374151]">
                With Plus, you get <span className="text-[#FBAF2C]">all subjects</span> plus 15 live tutor sessions per week.
              </p>
            </div>

            <div className="mt-1 flex w-full flex-col gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="display-text-semi w-full cursor-pointer rounded-xl bg-[#FBAF2C] py-3 text-sm text-white transition-colors hover:bg-[#E39E24]"
              >
                Go Back
              </button>
            </div>
          </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
